import React from "react";
import { Label } from "../../../../ui/label";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Textarea } from "../../../../ui/textarea";
import { Switch } from "../../../../ui/switch";
import { Badge } from "../../../../ui/badge";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import {
  GenerationConfig,
  SchemaField,
  SCHEMA_FIELD_TYPES,
  generateSchemaPreview,
  generateExamplePlaceholder,
} from "./types";

// ---------------------------------------------------------------------------
// Include Reasoning Toggle
// ---------------------------------------------------------------------------

export function IncludeReasoningToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (partial: Partial<GenerationConfig>) => void;
}) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg border border-border">
      <div className="space-y-1">
        <Label className="text-sm font-gc-med text-foreground">Include Reasoning Traces</Label>
        <p className="text-xs text-muted-foreground">
          Generate chain-of-thought reasoning with each example. Increases output quality and cost.
        </p>
      </div>
      <Switch
        checked={value}
        onCheckedChange={(checked) => onChange({ generation_include_reasoning: checked })}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Response Schema Builder (fields + add + preview + optional example)
// ---------------------------------------------------------------------------

export function ResponseSchemaBuilder({
  schema,
  onChange,
  responseExample,
  showExample = false,
  workflowTeacherResponseValuePath,
}: {
  schema: SchemaField[];
  onChange: (partial: Partial<GenerationConfig>) => void;
  responseExample?: string;
  showExample?: boolean;
  workflowTeacherResponseValuePath?: {
    value: string;
    onPathChange: (path: string) => void;
  };
}) {
  return (
    <div className="space-y-4 p-4 rounded-lg border border-border bg-muted/30">
      <div className="space-y-1">
        <Label className="text-sm font-gc-med text-foreground">
          Response Schema <span className="text-xs text-muted-foreground">(optional)</span>
        </Label>
        <p className="text-xs text-muted-foreground">
          Define a JSON schema if you want structured responses. Leave empty for free-form text output.
        </p>
      </div>
      <div className="space-y-2">
        {(schema || []).map((field, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <Input
              value={field.name}
              onChange={(e) => {
                const u = [...schema];
                u[idx] = { ...field, name: e.target.value };
                onChange({ generation_response_schema: u });
              }}
              placeholder="Field name"
              className="flex-1 min-w-0 text-sm font-mono"
            />
            <select
              value={field.type}
              onChange={(e) => {
                const u = [...schema];
                u[idx] = { ...field, type: e.target.value as SchemaField["type"] };
                onChange({ generation_response_schema: u });
              }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {SCHEMA_FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            <Input
              value={field.description}
              onChange={(e) => {
                const u = [...schema];
                u[idx] = { ...field, description: e.target.value };
                onChange({ generation_response_schema: u });
              }}
              placeholder="Description / constraints (optional)"
              className="flex-[2] min-w-0 text-sm"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange({ generation_response_schema: schema.filter((_, i) => i !== idx) })}
              className="h-9 w-9 p-0 shrink-0 text-muted-foreground hover:text-red-500"
            >
              <Trash2Icon className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange({ generation_response_schema: [...(schema || []), { name: "", type: "string", description: "" }] })}
        className="gap-1.5"
      >
        <PlusIcon className="w-3.5 h-3.5" /> Add Field
      </Button>
      {schema.length > 0 && schema.some((f) => f.name) && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground font-gc-med">Preview</Label>
          <pre className="text-xs font-mono text-foreground bg-background p-3 rounded-md border border-border overflow-x-auto whitespace-pre">
            {generateSchemaPreview(schema)}
          </pre>
        </div>
      )}
      {workflowTeacherResponseValuePath ? (
        <div className="space-y-1.5 pt-2 border-t border-border">
          <Label className="text-xs font-gc-med text-foreground">
            Response value path <span className="text-muted-foreground font-gc-reg">(optional)</span>
          </Label>
          <Input
            value={workflowTeacherResponseValuePath.value || ""}
            onChange={(e) => workflowTeacherResponseValuePath.onPathChange(e.target.value)}
            placeholder="e.g. hts10 or result.summary — dot path into workflow JSON output"
            className="font-mono text-sm h-9"
          />
          <p className="text-xs text-muted-foreground">
            When set, the assistant message for each example is this field from the workflow&apos;s JSON
            response (not the full JSON). Leave empty to use the raw workflow output string.
          </p>
        </div>
      ) : null}
      {showExample && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground font-gc-med">
            Example <span className="font-gc-reg">(optional)</span>
          </Label>
          <Textarea
            value={responseExample || ""}
            onChange={(e) => onChange({ generation_response_example: e.target.value })}
            placeholder={generateExamplePlaceholder(schema) || "{\n  \"field\": \"value\"\n}"}
            rows={4}
            className="font-mono text-xs"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Max Output Tokens
// ---------------------------------------------------------------------------

export function MaxTokensInput({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (partial: Partial<GenerationConfig>) => void;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-gc-med text-foreground">
        Max Output Tokens <span className="text-xs text-muted-foreground">(optional)</span>
      </Label>
      <div className="flex items-center gap-3">
        <Input
          type="number"
          min={1}
          max={128000}
          value={value ?? ""}
          onChange={(e) => onChange({ generation_max_tokens: e.target.value ? parseInt(e.target.value) : null })}
          placeholder="Model default"
          className="max-w-[200px]"
        />
        <div className="flex gap-1.5">
          {[256, 1024, 4096].map((v) => (
            <button
              key={v}
              onClick={() => onChange({ generation_max_tokens: value === v ? null : v })}
              className={cn(
                "px-2.5 py-1 rounded text-xs font-gc-med border transition-colors",
                value === v
                  ? "border-maitai-green bg-maitai-green/10 text-maitai-green"
                  : "border-border text-muted-foreground hover:border-maitai-green/50"
              )}
            >
              {v.toLocaleString()}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Caps the length of each generated response.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Response Example (standalone textarea)
// ---------------------------------------------------------------------------

export function ResponseExampleField({
  value,
  schema,
  onChange,
  showLabel = true,
}: {
  value: string;
  schema: SchemaField[];
  onChange: (partial: Partial<GenerationConfig>) => void;
  showLabel?: boolean;
}) {
  return (
    <div className="space-y-2">
      {showLabel && (
        <Label className="text-sm font-gc-med text-foreground">Response Example</Label>
      )}
      <Textarea
        value={value}
        onChange={(e) => onChange({ generation_response_example: e.target.value })}
        placeholder={
          schema.length > 0
            ? generateExamplePlaceholder(schema) || "{\n  \"field\": \"value\"\n}"
            : "e.g. The review expresses mixed sentiment — positive about battery life but negative about the trackpad."
        }
        rows={3}
        className={schema.length > 0 ? "font-mono text-xs" : ""}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template placeholder chips (shared by structured + teacher workflow input)
// ---------------------------------------------------------------------------

export function TemplatePlaceholderBadges({
  placeholders,
  color,
  label = "Available variables:",
}: {
  placeholders: string[];
  color: "purple" | "blue";
  label?: string;
}) {
  if (placeholders.length === 0) return null;
  const colorClasses =
    color === "purple"
      ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800"
      : "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800";
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {placeholders.map((p) => (
        <Badge key={p} variant="outline" className={`text-xs font-mono ${colorClasses}`}>
          {`{{${p}}}`}
        </Badge>
      ))}
    </div>
  );
}
